import { Button, Popover, Table, Typography } from "antd";
import styles from "../../../styles/Components/tables/portfolioTable.module.sass";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import React, { useState } from "react";
import useServiceStatus from "../../../hooks/useServiceStatus";
import {
  createWatchList,
  deleteWatchlist,
  modifyWatchlist,
} from "../../../redux/new-insight/actions";
import { IoNotifications, IoNotificationsOff } from "react-icons/io5";
import ServiceActivationModal from "../../../shared/service-activation/ServiceActivationModal";
import { formatPrice } from "../../../utility/helper";
import moment from "moment";
import { IoMdInformationCircleOutline } from "react-icons/io";

const { Text } = Typography;

function TrendsTable() {
  const {
    newInsightState: {
      overallStatistics: { all_important_data: trendsData = [] },
      getInitialDataLoading,
      watchlists,
      watchlistStatus,
      createWatchlistStatus,
      modifyWatchListStatus,
    },
    main: { userData },
  } = useSelector((state) => state);

  const [showServiceActivationModal, setShowServiceActivationModal] =
    useState(false);

  const [targetCoin, setTargetCoin] = useState("");

  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const sortValue = searchParams.get("sortBy") || "tops";
  const percentValue = searchParams.get("isPercent");
  const searchedTrends = searchParams.get("search") || "";

  const isPercentView =
    percentValue === "true" ? true : percentValue !== "false";

  const { isActive } = useServiceStatus("insight");
  const handleWatchlistActions = (isCoinInWatchlist, coin) => {
    if (watchlistStatus === "loading") return;

    // Prevent user to create watchlist if insight service is not active
    if (!isActive) return setShowServiceActivationModal(true);

    setTargetCoin(coin);

    if (watchlists.length === 0) {
      return dispatch(
        createWatchList(
          userData["custom:custom_username"],
          userData?.email,
          [coin],
          false,
        ),
      ).finally(() => setTargetCoin(""));
    }

    const currentWatchlist = watchlists?.[0];
    const createdDate = currentWatchlist?.createdDate;
    const coins =
      typeof currentWatchlist?.list_of_coins === "string"
        ? JSON.parse(currentWatchlist?.list_of_coins?.replace(/'/g, '"'))
        : currentWatchlist?.list_of_coins;

    if (coins.length === 1 && isCoinInWatchlist) {
      return dispatch(
        deleteWatchlist(
          userData["custom:custom_username"],
          userData?.email,
          createdDate,
          false,
        ),
      ).finally(() => setTargetCoin(""));
    }

    const updatedCoinsList = isCoinInWatchlist
      ? coins.filter((c) => c !== coin)
      : [...coins, coin];
    dispatch(
      modifyWatchlist(
        userData["custom:custom_username"],
        userData?.email,
        createdDate,
        updatedCoinsList,
        false,
      ),
    ).finally(() => setTargetCoin(""));
  };

  const displayTrendsData = [...trendsData]
    ?.filter(
      (item) =>
        item.name?.toLowerCase()?.includes(searchedTrends) ||
        (item?.suggestions || "")?.toLowerCase().includes(searchedTrends) ||
        (item?.sentiment_label || "")?.toLowerCase().includes(searchedTrends),
    )
    .sort((a, b) => {
      switch (sortValue) {
        case "tops":
          return a.market_cap_rank - b.market_cap_rank; // Ascending by market rank
        case "gainers":
          return b.market_cap_change_24h - a.market_cap_change_24h; // Descending by market cap change
        case "losers":
          return a.market_cap_change_24h - b.market_cap_change_24h; // Ascending by market cap change
        default:
          return 0; // No sorting for unknown sortValue
      }
    });

  const columns = [
    {
      title: "#",
      dataIndex: "market_cap_rank",
      key: "market_cap_rank",
      align: "center",
      width: 15,
      render: (text) => (
        <span style={{ color: "#0C4CFC", fontFamily: "Poppins-Medium" }}>
          #{text}
        </span>
      ),
    },
    {
      title: "Coin",
      dataIndex: "symbol",
      key: "symbol",
      align: "center",
      width: 200,
      render: (text, record) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={record.image}
            alt={record.name}
            style={{ width: 20, height: 20, marginRight: ".2rem" }}
          />
          <span>{record?.name}</span>
          <span
            style={{
              fontSize: "12px",
              marginLeft: ".5rem",
              color: "gray",
            }}
          >
            {text}
          </span>
        </div>
      ),
    },
    {
      title: "Current Price",
      dataIndex: "current_price",
      key: "current_price",
      align: "center",
      render: (price, record) => {
        const displayData = isPercentView
          ? record.price_change_percentage_24h
          : record.price_change_24h;

        const isValidData = typeof displayData === "number";

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <span>${formatPrice(price, 4)}</span>
            <Text
              type={isValidData ? (displayData > 0 ? "success" : "danger") : ""}
            >
              {!isValidData
                ? "-"
                : isPercentView
                  ? `${Number(displayData).toFixed(2)}%`
                  : `$${formatPrice(displayData, 2)}`}
            </Text>
          </div>
        );
      },
      sorter: (a, b) =>
        b.price_change_percentage_24h - a.price_change_percentage_24h,
    },
    {
      title: "Market Cap",
      dataIndex: "market_cap",
      key: "market_cap",
      align: "center",
      render: (cap, record) => {
        const value = isPercentView
          ? record.market_cap_change_percentage_24h
          : record.market_cap_change_24h;

        const isValidData = typeof value === "number";

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            {/*<span>${(cap / 1_000_000_000).toFixed(2)}B</span>*/}
            <span>${formatPrice(cap)}</span>
            <Text type={isValidData ? (value >= 0 ? "success" : "danger") : ""}>
              {isValidData
                ? isPercentView
                  ? `${value.toFixed(2)}%`
                  : `$${formatPrice(value, 2)}`
                : "-"}
            </Text>
          </div>
        );
      },
      sorter: (a, b) => b.market_cap - a.market_cap,
    },
    {
      title: "Social Sentiment",
      dataIndex: "sentiment_label",
      key: "sentiment_label",
      align: "center",
      width: 180,
      render: (text, record) => {
        const status = record?.google_trends_interest_change;
        const change =
          typeof status === "number" ? status?.toFixed(2) + "%" : status;

        const className =
          text === "NEGATIVE"
            ? "negative"
            : text === "NEUTRAL"
              ? "neutral"
              : text === "POSITIVE"
                ? "positive"
                : "";

        const date = record?.sentiment_date;
        const formattedDate = date ? moment(date)?.format("MMM DD") : "";
        const suggestion = record?.suggestions || "";
        const suggestionPreview = suggestion?.slice(0, 20) + "...";

        return (
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              flexDirection: "column",
            }}
          >
            {text ? (
              <span className={`${styles.sentiment} ${styles?.[className]}`}>
                {text}
              </span>
            ) : (
              <span style={{ alignSelf: "center" }}>No data</span>
            )}

            {!!date && (
              <span
                style={{
                  fontSize: "12px",
                  alignSelf: "flex-start",
                  color: "gray",
                }}
              >
                {formattedDate}
              </span>
            )}
            {!!suggestion && (
              <Popover
                className={styles.sentimentSuggestion}
                content={<p>{suggestion}</p>}
              >
                {suggestionPreview}
                <IoMdInformationCircleOutline color="#0C4CFC" size={14} />
              </Popover>
            )}

            {/*{typeof status === "number" && (*/}
            {/*  <Text type={change >= 0 ? "success" : "danger"}>{change}</Text>*/}
            {/*)}*/}
          </div>
        );
      },
      sorter: (a, b) => {
        const getScore = (item) => {
          const raw = item?.sentiment_score ?? item?.sentiment_score;
          if (raw === undefined || raw === null || raw === "")
            return Number.NEGATIVE_INFINITY;
          const n = typeof raw === "number" ? raw : parseFloat(raw);
          return Number.isFinite(n) ? n : Number.NEGATIVE_INFINITY;
        };

        return getScore(b) - getScore(a);
      },
    },
    {
      title: "Geopolitical Risk",
      dataIndex: "geopolitical_risk_label",
      key: "geopolitical_risk_label",
      align: "center",
      render: (text, record) => {
        const color =
          text === "MEDIUM"
            ? "#ff9800"
            : text === "LOW"
              ? "#198754"
              : text === "HIGH"
                ? "#e74c3c"
                : "";

        const gplScore = record?.geopolitical_risk_score;
        // const riskStatus = text === "False" ? "Safe" : "Risky";
        return (
          <>
            <div
              style={{ color: `${color}`, textAlign: "center" }}

              // type={text === "False" ? "success" : "danger"}
            >
              {text ? text : "No data"}
              {!!gplScore && (
                <span
                  style={{
                    fontSize: "12px",
                    color: "grey",
                    marginLeft: ".3rem",
                  }}
                >
                  ({gplScore})
                </span>
              )}
            </div>
          </>
        );
      },
      sorter: (a, b) => {
        const getScore = (item) => {
          const raw = item?.geopolitical_risk_score ?? item?.geopolitical_risk;
          if (raw === undefined || raw === null || raw === "")
            return Number.NEGATIVE_INFINITY;
          const n = typeof raw === "number" ? raw : parseFloat(raw);
          return Number.isFinite(n) ? n : Number.NEGATIVE_INFINITY;
        };

        return getScore(b) - getScore(a);
      },
    },
    {
      title: "Notification",
      key: "notification",
      align: "center",
      render: (_, record) => {
        const symbol = record.symbol.toUpperCase();
        const isCoinInList = watchlists?.[0]?.list_of_coins?.includes(symbol); // Check if the coin is in the list

        return (
          <Button
            className={styles.buyNow}
            disabled={watchlistStatus === "loading"}
            loading={
              targetCoin === symbol
                ? createWatchlistStatus === "loading" ||
                  modifyWatchListStatus === "loading"
                : false
            }
            onClick={() => handleWatchlistActions(isCoinInList, symbol)}
          >
            {isCoinInList ? (
              <IoNotificationsOff /> // Show IoNotificationsOff if in the list
            ) : (
              <IoNotifications /> // Show IoNotifications if not in the list
            )}
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <Table
        className={styles.table}
        columns={columns}
        dataSource={displayTrendsData}
        loading={getInitialDataLoading}
        scroll={{ x: 320 }}
      />

      {showServiceActivationModal && (
        <ServiceActivationModal
          service={"insai"}
          show={showServiceActivationModal}
          setShow={setShowServiceActivationModal}
        />
      )}
    </>
  );
}

export default TrendsTable;
